

import React, { useState, useEffect, useCallback } from "react";
import {
  Box, Card, CardContent, Grid, Typography, Button, TextField,
  MenuItem, Table, TableHead, TableBody, TableRow, TableCell, Chip,
  Avatar, IconButton, Tooltip, CircularProgress, Alert, Dialog,
  DialogTitle, DialogContent, DialogActions, InputAdornment, Divider,
} from "@mui/material";
import {
  Add, Search, Edit, Delete, Visibility, Group, CheckCircle,
  Work, Business, FileDownload,
} from "@mui/icons-material";
import { ThemeProvider } from "@mui/material/styles";
import EmployeeDetail from "./Employeedetail";
import {
  EMP_STATUSES, EMPLOYMENT_TYPES, CURRENCIES, DEPARTMENTS,
  STATUS_COLOR, DEPT_COLOR, EMPTY_FORM,
  fmtDate, fmtMoney, nameInitials, errText,
} from "./employeeConstants";
import recruiterTheme, {
  FONT_BODY, FONT_DISPLAY, COLORS, TYPE, RADIUS, SHADOW, CONTROL_HEIGHT, ELLIPSIS, humanizeStatus,
} from "../../../styles/recruiterTheme";
// ── API helpers ───────────────────────────────────────────────────────────────
const BASE = process.env.REACT_APP_API_BASE_URL;
const EMPLOYEE_BASE = process.env.REACT_APP_API_EMPLOYEES_URL;
const hdrs = () => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${localStorage.getItem("token") || ""}`,
});
const CLIENT_BASE = process.env.REACT_APP_API_CLIENTS_URL;
// Tolerates empty / non-JSON bodies (e.g. a 204 on DELETE or an HTML 502 page).
const ok = async (res) => {
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw (data && Object.keys(data).length ? data : new Error(`Request failed (${res.status})`));
  return data;
};

const getAllEmployees = (params = {}) => {
  const qs = new URLSearchParams(params).toString();
  return fetch(`${EMPLOYEE_BASE}/${qs ? "?" + qs : ""}`, { headers: hdrs() }).then(ok);
};
const createEmployee = (pl)     => fetch(`${EMPLOYEE_BASE}/`,      { method: "POST",   headers: hdrs(), body: JSON.stringify(pl) }).then(ok);
const updateEmployee = (id, pl) => fetch(`${EMPLOYEE_BASE}/${id}`, { method: "PUT",    headers: hdrs(), body: JSON.stringify(pl) }).then(ok);
const deleteEmployee = (id)     => fetch(`${EMPLOYEE_BASE}/${id}`, { method: "DELETE", headers: hdrs() }).then(ok);

const downloadExport = async (type = "excel") => {
  const res  = await fetch(`${BASE}/export/employees/${type}`, { headers: hdrs() });
  if (!res.ok) throw new Error("Export failed");
  const blob = await res.blob();
  const link = document.createElement("a");
  link.href     = URL.createObjectURL(blob);
  link.download = `employees_${new Date().toISOString().slice(0, 10)}.${type === "csv" ? "csv" : "xlsx"}`;
  link.click();
  // Revoking synchronously can cancel the download in some browsers.
  setTimeout(() => URL.revokeObjectURL(link.href), 1000);
};

// ── Shared sx ─────────────────────────────────────────────────────────────────
const TF = { width: "100%", minWidth: 250 };
const btnSx = { height: CONTROL_HEIGHT.md, textTransform: "none", fontWeight: 600, borderRadius: `${RADIUS.sm}px`, px: 2 };
const primaryBtnSx = { ...btnSx, bgcolor: COLORS.primary, "&:hover": { bgcolor: COLORS.primaryDark } };
const cardSx = { bgcolor: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: `${RADIUS.lg}px`, boxShadow: SHADOW.card };
const headSx = {
  fontFamily: FONT_BODY, fontSize: TYPE.overline, fontWeight: 700, color: COLORS.muted, textTransform: "uppercase",
  letterSpacing: "0.06em", whiteSpace: "nowrap", bgcolor: COLORS.surfaceTint, borderBottom: `1px solid ${COLORS.border}`,
  py: 1.5, px: 2,
};
const cellSx = { py: 1.5, px: 2, borderBottom: `1px solid ${COLORS.border}` };
const dialogTitleSx = { borderBottom: `1px solid ${COLORS.border}` };
const dialogActionsSx = { px: 3, py: 2, borderTop: `1px solid ${COLORS.border}` };
// Column widths (sum ≈ 1064px) so the table fits a 1080px content area without scrolling.
const COLS = [
  // Employee and Current Client have no fixed width: they share whatever the dashboard leaves
  // (the HR sidebar is 300px, the manager one 240px), so the table never clips its Actions column.
  { h: "Employee" }, { h: "Department", w: 128 }, { h: "Current Client" },
  { h: "Salary", w: 88, align: "right" }, { h: "Billing", w: 96, align: "right" },
  { h: "Joined", w: 112 }, { h: "Status", w: 104 }, { h: "Actions", w: 112, align: "right" },
];

// ── Stat Card ─────────────────────────────────────────────────────────────────
function StatCard({ title, value, color, icon }) {
  return (
    <Card elevation={0} sx={{ ...cardSx, borderRadius: `${RADIUS.md}px`, overflow: "hidden", position: "relative", height: "100%" }}>
      <Box sx={{ position: "absolute", left: 0, top: 0, bottom: 0, width: 4, bgcolor: color }} />
      <CardContent sx={{ p: 2, pl: 3, "&:last-child": { pb: 2 } }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" gap={2}>
          <Box minWidth={0}>
            <Typography sx={{ fontFamily: FONT_BODY, fontSize: TYPE.stat, fontWeight: 800, color: COLORS.primaryDeep, lineHeight: 1, fontVariantNumeric: "tabular-nums" }}>
              {value}
            </Typography>
            <Typography sx={{ fontFamily: FONT_BODY, fontSize: TYPE.overline, fontWeight: 700, color: COLORS.muted, textTransform: "uppercase", letterSpacing: "0.06em", mt: 1, ...ELLIPSIS }}>
              {title}
            </Typography>
          </Box>
          <Box sx={{ width: 40, height: 40, borderRadius: `${RADIUS.md}px`, bgcolor: `${color}18`, flexShrink: 0,
                     display: "flex", alignItems: "center", justifyContent: "center" }}>
            {React.cloneElement(icon, { sx: { fontSize: 20, color } })}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}

// ── Form section label ────────────────────────────────────────────────────────
const FormSection = ({ children }) => (
  <Typography fontWeight={700} fontSize={TYPE.overline} color={COLORS.primary}
    textTransform="uppercase" letterSpacing="0.06em" mb={2}>
    {children}
  </Typography>
);

// ── Employee Add / Edit Form ──────────────────────────────────────────────────
function EmployeeForm({ open, onClose, onSave, selected, saving, error }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [clientNames, setClientNames] = useState([]);

  useEffect(() => {
    if (!open) return;
    fetch(`${CLIENT_BASE}/names/list`)
      .then(r => r.json())
      .then(d => { if (d?.success && Array.isArray(d.data)) setClientNames(d.data); })
      .catch(() => setClientNames(["ZentreeLabs Pvt Ltd"]));
  }, [open]);
  useEffect(() => {
    if (!open) return;
    if (selected) {
      setForm({
        ...EMPTY_FORM, ...selected,
        date_of_joining:      selected.date_of_joining      ? String(selected.date_of_joining).split("T")[0] : "",
        salary:               selected.salary               || "",
        current_billing_rate: selected.current_billing_rate || "",
        experience:           selected.experience           || "",
      });
    } else {
      setForm(EMPTY_FORM);
    }
  }, [open, selected]);

  const handle = e => setForm(p => ({ ...p, [e.target.name]: e.target.value }));

  const submit = e => {
    e.preventDefault();
    if (saving) return;
    onSave({
      ...form,
      experience:           form.experience           ? Number(form.experience)           : 0,
      salary:               form.salary               ? Number(form.salary)               : 0,
      current_billing_rate: form.current_billing_rate ? Number(form.current_billing_rate) : 0,
    });
  };

  // Keep the saved client selectable even if it is no longer in the clients list.
  const clientOptions = form.current_client && !clientNames.includes(form.current_client)
    ? [form.current_client, ...clientNames] : clientNames;

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="md" fullWidth
      PaperProps={{ sx: { borderRadius: `${RADIUS.lg}px` } }}>
      <DialogTitle sx={dialogTitleSx}>
        {selected ? "Edit Employee" : "Add New Employee"}
      </DialogTitle>

      <form onSubmit={submit}>
        <DialogContent sx={{ pt: "24px !important" }}>
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}
          {/* ── Personal Information ───────────────────────────────────────── */}
          <FormSection>Personal Information</FormSection>
          <Grid container spacing={2} mb={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                size="small" required label="Employee ID" name="emp_id"
                value={form.emp_id} onChange={handle}
                // disabled={!!selected}
                placeholder="e.g. EMP001"
                helperText="Unique ID assigned to this employee"
                sx={TF}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                size="small" required label="Full Name" name="name"
                value={form.name} onChange={handle} sx={TF}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                size="small" required type="email" label="Work Email" name="email"
                value={form.email} onChange={handle} sx={TF}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                size="small" label="Phone Number" name="phone"
                value={form.phone} onChange={handle}
                placeholder="+91 98765 43210" sx={TF}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                size="small" label="Location / City" name="location"
                value={form.location} onChange={handle}
                placeholder="e.g. Hyderabad" sx={TF}
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          {/* ── Employment Details ─────────────────────────────────────────── */}
          <FormSection>Employment Details</FormSection>
          <Grid container spacing={2} mb={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                size="small" label="Designation / Job Title" name="designation"
                value={form.designation} onChange={handle}
                placeholder="e.g. Senior Software Engineer" sx={TF}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select size="small" label="Department" name="department"
                value={form.department} onChange={handle} sx={TF}>
                {DEPARTMENTS.map(d => <MenuItem key={d} value={d}>{d}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select size="small" label="Employment Type" name="employment_type"
                value={form.employment_type} onChange={handle} sx={TF}>
                {EMPLOYMENT_TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                size="small" type="date" label="Date of Joining" name="date_of_joining"
                value={form.date_of_joining} onChange={handle}
                InputLabelProps={{ shrink: true }} sx={TF}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                size="small" type="number" label="Experience (years)" name="experience"
                value={form.experience} onChange={handle}
                inputProps={{ min: 0, step: 0.01 }}
                placeholder="e.g. 3.5" sx={TF}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                size="small" label="Reporting Manager" name="reporting_manager"
                value={form.reporting_manager} onChange={handle}
                placeholder="Manager's full name" sx={TF}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select size="small" label="Employment Status" name="status"
                value={form.status} onChange={handle} sx={TF}>
                {EMP_STATUSES.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                size="small" type="number" label="Annual Salary (₹)" name="salary"
                value={form.salary} onChange={handle}
                inputProps={{ min: 0 }}
                placeholder="e.g. 800000" sx={TF}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                size="small" label="Skills (comma-separated)" name="skills"
                value={form.skills} onChange={handle}
                placeholder="e.g. React, Node.js, MongoDB, AWS" sx={TF}
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          {/* ── Current Client Deployment ──────────────────────────────────── */}
          <FormSection>Current Client Deployment</FormSection>
          <Grid container spacing={2} mb={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                select size="small" label="Current Client" name="current_client"
                value={form.current_client || ""} onChange={handle} sx={TF}>
                <MenuItem value="">— None —</MenuItem>
                {clientOptions.map(name => (
                  <MenuItem key={name} value={name}>{name}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                size="small" label="Current Project" name="current_project"
                value={form.current_project} onChange={handle}
                placeholder="Project name" sx={TF}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                size="small" type="number" label="Billing Rate" name="current_billing_rate"
                value={form.current_billing_rate} onChange={handle}
                inputProps={{ min: 0 }}
                placeholder="e.g. 75" sx={TF}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select size="small" label="Billing Currency" name="billing_currency"
                value={form.billing_currency} onChange={handle} sx={TF}>
                {CURRENCIES.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
              </TextField>
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          {/* ── Notes ─────────────────────────────────────────────────────── */}
          <FormSection>Additional Notes</FormSection>
          <TextField
            multiline rows={3} size="small" label="Notes"
            name="notes" value={form.notes} onChange={handle}
            placeholder="Any additional remarks about this employee…"
            sx={TF}
          />
        </DialogContent>

        <DialogActions sx={dialogActionsSx}>
          <Button onClick={onClose} disabled={saving} sx={{ ...btnSx, color: COLORS.muted }}>Cancel</Button>
          <Button type="submit" variant="contained" disabled={saving} sx={primaryBtnSx}>
            {saving && <CircularProgress size={16} sx={{ mr: 1, color: "#fff" }} />}
            {selected ? "Update Employee" : "Add Employee"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

// ── Centered state inside a card (empty / loading / error) ────────────────────
const CardState = ({ children }) => (
  <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" textAlign="center" py={8} px={3} gap={2}>
    {children}
  </Box>
);

// ── Employee Table ────────────────────────────────────────────────────────────
function EmployeeTable({ employees, filtered, onView, onEdit, onDelete, onAdd }) {
  if (employees.length === 0) {
    return (
      <Card elevation={0} sx={cardSx}>
        <CardState>
          <Avatar sx={{ width: 64, height: 64, bgcolor: COLORS.accentSoft }}>
            <Group sx={{ fontSize: 32, color: COLORS.primary }} />
          </Avatar>
          <Typography fontSize={TYPE.cardTitle} fontWeight={700} color={COLORS.ink}>No employees yet</Typography>
          <Typography fontSize={TYPE.body} color={COLORS.muted}>
            Add your first employee to start tracking workforce data.
          </Typography>
          <Button variant="contained" startIcon={<Add />} onClick={onAdd} sx={primaryBtnSx}>
            Add Employee
          </Button>
        </CardState>
      </Card>
    );
  }

  const now = new Date();
  return (
    <Card elevation={0} sx={{ ...cardSx, overflowX: "auto" }}>
      <Table sx={{ tableLayout: "fixed", width: "100%", minWidth: 880 }}>
        <colgroup>{COLS.map(c => <col key={c.h} style={{ width: c.w }} />)}</colgroup>
        <TableHead>
          <TableRow>
            {COLS.map(c => (
              <TableCell key={c.h} align={c.align || "left"} sx={headSx}>{c.h}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {filtered.length === 0 ? (
            <TableRow>
              <TableCell colSpan={COLS.length} align="center" sx={{ py: 6, color: COLORS.muted, fontSize: TYPE.body }}>
                No employees match your filters
              </TableCell>
            </TableRow>
          ) : filtered.map(emp => {
            const dc = DEPT_COLOR[emp.department] || "#475569";
            const subline = [emp.emp_id, emp.designation].filter(Boolean).join(" · ");
            const endDate = emp.current_engagement_end_date ? new Date(emp.current_engagement_end_date) : null;
            return (
              <TableRow key={emp._id} hover sx={{ "&:last-child td": { borderBottom: 0 } }}>
                <TableCell sx={cellSx}>
                  <Box display="flex" alignItems="center" gap={1.5} minWidth={0}>
                    <Avatar sx={{ width: 36, height: 36, fontSize: TYPE.caption, fontWeight: 700, bgcolor: dc, flexShrink: 0 }}>
                      {nameInitials(emp.name)}
                    </Avatar>
                    <Box minWidth={0}>
                      <Typography fontWeight={600} fontSize={TYPE.small} color={COLORS.ink} title={emp.name} sx={ELLIPSIS}>
                        {emp.name || "—"}
                      </Typography>
                      <Typography fontSize={TYPE.caption} color={COLORS.muted} title={subline} sx={ELLIPSIS}>
                        {subline || "—"}
                      </Typography>
                    </Box>
                  </Box>
                </TableCell>
                <TableCell sx={cellSx}>
                  {emp.department ? (
                    <Chip label={emp.department} size="small" title={emp.department}
                      sx={{ bgcolor: `${dc}18`, color: dc, maxWidth: "100%" }} />
                  ) : <Typography fontSize={TYPE.small} color={COLORS.faint}>—</Typography>}
                </TableCell>
                <TableCell sx={cellSx}>
                  {emp.current_client ? (
                    <Box minWidth={0}>
                      <Typography fontSize={TYPE.small} fontWeight={700} color={COLORS.success} title={emp.current_client} sx={ELLIPSIS}>
                        {emp.current_client}
                      </Typography>
                      {emp.current_project && (
                        <Typography fontSize={TYPE.caption} color={COLORS.muted} title={emp.current_project} sx={ELLIPSIS}>
                          {emp.current_project}
                        </Typography>
                      )}
                      <Typography fontSize={TYPE.caption} fontWeight={endDate ? 600 : 400} sx={{ whiteSpace: "nowrap" }}
                        color={endDate ? (endDate < now ? COLORS.error : COLORS.warning) : COLORS.faint}>
                        {endDate ? `Until ${fmtDate(emp.current_engagement_end_date)}` : "Open-ended"}
                      </Typography>
                    </Box>
                  ) : <Typography fontSize={TYPE.small} color={COLORS.faint}>—</Typography>}
                </TableCell>
                <TableCell align="right" sx={{ ...cellSx, whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}>
                  {Number(emp.salary) > 0 ? `${(Number(emp.salary) / 100000).toFixed(1)} LPA` : "—"}
                </TableCell>
                <TableCell align="right" sx={{ ...cellSx, whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}>
                  {Number(emp.current_billing_rate) > 0
                    ? <Typography component="span" fontSize={TYPE.small} fontWeight={700} color={COLORS.success}>
                        {fmtMoney(emp.current_billing_rate, emp.billing_currency)}
                      </Typography>
                    : <Typography component="span" fontSize={TYPE.small} color={COLORS.faint}>—</Typography>}
                </TableCell>
                <TableCell sx={cellSx}>
                  <Typography fontSize={TYPE.small} color={COLORS.ink} sx={{ whiteSpace: "nowrap" }}>{fmtDate(emp.date_of_joining)}</Typography>
                  <Typography fontSize={TYPE.caption} color={COLORS.muted} sx={{ whiteSpace: "nowrap" }}>
                    {emp.experience || emp.experience === 0 ? `${emp.experience} yrs exp.` : "—"}
                  </Typography>
                </TableCell>
                <TableCell sx={cellSx}>
                  <Chip label={humanizeStatus(emp.status) || "—"} color={STATUS_COLOR[emp.status] || "default"}
                    size="small" sx={{ maxWidth: "100%" }} />
                </TableCell>
                <TableCell align="right" sx={cellSx}>
                  <Box display="flex" gap={0.5} justifyContent="flex-end">
                    <Tooltip title="View details">
                      <IconButton size="small" onClick={() => onView(emp)} sx={{ color: COLORS.primary }}><Visibility fontSize="small" /></IconButton>
                    </Tooltip>
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => onEdit(emp)} sx={{ color: COLORS.muted }}><Edit fontSize="small" /></IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton size="small" color="error" onClick={() => onDelete(emp)}><Delete fontSize="small" /></IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Card>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
// Rendered only inside a dashboard tab (HR + Manager), which supplies the canvas and
// PAGE_PADDING — so this component adds no outer padding of its own.
export default function Employees() {
  const [employees,  setEmployees]  = useState([]);
  const [loaded,     setLoaded]     = useState(false);   // first fetch finished (success or not)
  const [loading,    setLoading]    = useState(true);
  const [loadError,  setLoadError]  = useState("");      // list could not be fetched
  const [error,      setError]      = useState("");      // banner for other page-level failures
  const [search,     setSearch]     = useState("");
  const [statusF,    setStatusF]    = useState("");
  const [deptF,      setDeptF]      = useState("");
  const [formOpen,   setFormOpen]   = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [selected,   setSelected]   = useState(null);
  const [saving,     setSaving]     = useState(false);
  const [deleting,   setDeleting]   = useState(false);
  const [deleteError, setDeleteError] = useState("");
  const [exporting,  setExporting]  = useState("");      // "excel" | "csv" | ""
  const [formError,  setFormError]  = useState("");

  // Refetch keeps the current list on screen (no full-page spinner that would
  // unmount open dialogs); only the very first load shows the spinner.
  const load = useCallback(async () => {
    setLoading(true); setLoadError("");
    try {
      const res = await getAllEmployees();
      setEmployees(Array.isArray(res?.data) ? res.data : []);
      return res?.data || [];
    } catch (err) {
      setLoadError(errText(err, "Failed to load employees"));
      return null;
    } finally {
      setLoading(false);
      setLoaded(true);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = employees
    .filter((e, idx, arr) => arr.findIndex(x => x._id === e._id) === idx)
    .filter(e => {
      const q  = search.toLowerCase();
      const mQ = !q || [e.name, e.emp_id, e.designation, e.current_client]
                        .some(f => f != null && String(f).toLowerCase().includes(q));
      const mS = !statusF || e.status     === statusF;
      const mD = !deptF   || e.department === deptF;
      return mQ && mS && mD;
    });

  const stats = {
    total:   employees.length,
    active:  employees.filter(e => e.status === "Active").length,
    onBench: employees.filter(e => e.status === "On Bench").length,
    clients: new Set(employees.filter(e => e.current_client).map(e => e.current_client)).size,
  };

  const openCreate = ()  => { setSelected(null); setFormError(""); setFormOpen(true); };
  const openEdit   = (e) => { setSelected(e);    setFormError(""); setFormOpen(true); setDetailOpen(false); };
  const openView   = (e) => { setSelected(e);    setDetailOpen(true); };
  const openDelete = (e) => { setSelected(e);    setDeleteError(""); setDeleteOpen(true); };

  const handleSave = async (payload) => {
    if (saving) return;
    setSaving(true);
    setFormError("");
    try {
      if (selected) await updateEmployee(selected._id, payload);
      else          await createEmployee(payload);
      setFormOpen(false);
      load();
    } catch (err) {
      setFormError(errText(err, "Save failed"));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (deleting || !selected) return;
    setDeleting(true); setDeleteError("");
    try {
      await deleteEmployee(selected._id);
      setDeleteOpen(false);
      setEmployees(prev => prev.filter(e => e._id !== selected._id));
      load();
    } catch (err) {
      setDeleteError(errText(err, "Delete failed"));
    } finally {
      setDeleting(false);
    }
  };

  // Called by the detail dialog after it mutates an employee; never rejects.
  const handleEmployeeUpdate = async () => {
    const fresh = await load();
    if (!fresh) return;
    const updated = fresh.find(e => e._id === selected?._id);
    if (updated) setSelected(updated);
  };

  const handleExport = async (type) => {
    if (exporting) return;
    setExporting(type); setError("");
    try   { await downloadExport(type); }
    catch { setError("Export failed — please try again."); }
    finally { setExporting(""); }
  };

  const body = () => {
    if (!loaded && loading) return (
      <Card elevation={0} sx={cardSx}>
        <CardState>
          <CircularProgress size={32} sx={{ color: COLORS.primary }} />
          <Typography fontSize={TYPE.body} color={COLORS.muted}>Loading employees…</Typography>
        </CardState>
      </Card>
    );
    if (loadError && employees.length === 0) return (
      <Card elevation={0} sx={cardSx}>
        <CardState>
          <Typography fontSize={TYPE.body} color={COLORS.error}>{loadError}</Typography>
          <Button variant="outlined" onClick={load} disabled={loading} sx={btnSx}>
            {loading ? "Retrying…" : "Retry"}
          </Button>
        </CardState>
      </Card>
    );
    return (
      <EmployeeTable
        employees={employees} filtered={filtered}
        onView={openView} onEdit={openEdit} onDelete={openDelete} onAdd={openCreate}
      />
    );
  };

  return (
    <ThemeProvider theme={recruiterTheme}>
    <Box display="flex" flexDirection="column" gap={3} sx={{ fontFamily: FONT_BODY, color: COLORS.ink }}>

      {/* ── Page Header ──────────────────────────────────────────────────── */}
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={2}>
        <Box minWidth={0}>
          <Typography sx={{ fontFamily: FONT_DISPLAY, fontSize: TYPE.page, fontWeight: 700, color: COLORS.primaryDeep, lineHeight: 1.25 }}>
            Our Employees
          </Typography>
          <Typography fontSize={TYPE.body} color={COLORS.muted} mt={0.5}>
            Track profiles, onboarding progress, documents, and client deployments
          </Typography>
        </Box>
        <Box display="flex" gap={1} flexWrap="wrap" alignItems="center">
          <Button variant="outlined" disabled={!!exporting}
            startIcon={exporting === "excel" ? <CircularProgress size={14} /> : <FileDownload />}
            onClick={() => handleExport("excel")}
            sx={{ ...btnSx, borderColor: COLORS.success, color: COLORS.success }}>
            Export Excel
          </Button>
          <Button variant="outlined" disabled={!!exporting}
            startIcon={exporting === "csv" ? <CircularProgress size={14} /> : <FileDownload />}
            onClick={() => handleExport("csv")}
            sx={{ ...btnSx, borderColor: COLORS.borderStrong, color: COLORS.muted }}>
            Export CSV
          </Button>
          <Button variant="contained" startIcon={<Add />} onClick={openCreate} sx={primaryBtnSx}>
            Add Employee
          </Button>
        </Box>
      </Box>

      {error && <Alert severity="error" onClose={() => setError("")}>{error}</Alert>}
      {loadError && employees.length > 0 && (
        <Alert severity="error" action={<Button size="small" onClick={load} disabled={loading} sx={{ textTransform: "none", fontWeight: 600 }}>Retry</Button>}>
          {loadError}
        </Alert>
      )}

      {/* ── Stats ────────────────────────────────────────────────────────── */}
      <Grid container spacing={2}>
        <Grid item xs={6} md={3}><StatCard title="Total Employees" value={stats.total}   color={COLORS.primary} icon={<Group />} /></Grid>
        <Grid item xs={6} md={3}><StatCard title="Active"          value={stats.active}  color={COLORS.success} icon={<CheckCircle />} /></Grid>
        <Grid item xs={6} md={3}><StatCard title="On Bench"        value={stats.onBench} color={COLORS.warning} icon={<Work />} /></Grid>
        <Grid item xs={6} md={3}><StatCard title="Active Clients"  value={stats.clients} color={COLORS.info}    icon={<Business />} /></Grid>
      </Grid>

      {/* ── Filters ──────────────────────────────────────────────────────── */}
      <Box display="flex" gap={2} flexWrap="wrap" alignItems="center">
        <TextField
          placeholder="Search by name, ID, designation, client…"
          value={search} onChange={e => setSearch(e.target.value)}
          size="small" sx={{ flexGrow: 1, minWidth: 248, bgcolor: COLORS.surface }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" sx={{ color: COLORS.muted }} />
              </InputAdornment>
            ),
          }}
        />
        <TextField select value={statusF} onChange={e => setStatusF(e.target.value)}
          size="small" sx={{ width: "100%", maxWidth: 176, minWidth: 152, bgcolor: COLORS.surface }} label="Status">
          <MenuItem value="">All Statuses</MenuItem>
          {EMP_STATUSES.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
        </TextField>
        <TextField select value={deptF} onChange={e => setDeptF(e.target.value)}
          size="small" sx={{ width: "100%", maxWidth: 216, minWidth: 176, bgcolor: COLORS.surface }} label="Department">
          <MenuItem value="">All Departments</MenuItem>
          {DEPARTMENTS.map(d => <MenuItem key={d} value={d}>{d}</MenuItem>)}
        </TextField>
        {(search || statusF || deptF) && (
          <Button size="small" onClick={() => { setSearch(""); setStatusF(""); setDeptF(""); }}
            sx={{ textTransform: "none", fontWeight: 600, color: COLORS.muted }}>
            Clear filters
          </Button>
        )}
      </Box>

      {/* ── Table ────────────────────────────────────────────────────────── */}
      {body()}

      {/* ── Detail Dialog ────────────────────────────────────────────────── */}
      <EmployeeDetail
        open={detailOpen} employee={selected}
        onClose={() => setDetailOpen(false)}
        onEdit={openEdit} onEmployeeUpdate={handleEmployeeUpdate}
      />

      {/* ── Add / Edit Form ──────────────────────────────────────────────── */}
      <EmployeeForm
        open={formOpen} onClose={() => setFormOpen(false)}
        onSave={handleSave} selected={selected} saving={saving}
        error={formError}
      />

      {/* ── Delete Confirm ───────────────────────────────────────────────── */}
      <Dialog open={deleteOpen} onClose={deleting ? undefined : () => setDeleteOpen(false)} maxWidth="xs" fullWidth
        PaperProps={{ sx: { borderRadius: `${RADIUS.lg}px` } }}>
        <DialogTitle>Delete Employee</DialogTitle>
        <DialogContent>
          {deleteError && <Alert severity="error" sx={{ mb: 2 }}>{deleteError}</Alert>}
          <Typography fontSize={TYPE.body} color={COLORS.ink}>
            Are you sure you want to permanently delete <strong>{selected?.name}</strong>?
            This will also remove all their onboarding and document records.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteOpen(false)} disabled={deleting} sx={{ ...btnSx, color: COLORS.muted }}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDelete} disabled={deleting} sx={btnSx}>
            {deleting && <CircularProgress size={16} sx={{ mr: 1, color: "#fff" }} />}
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
    </ThemeProvider>
  );
}
